import { type ChangeEvent, useState } from 'react';

// Shared password input — same .field styling as the other auth
// fields, with a show/hide toggle on the right. Used by signup,
// login, and reset-password so the chrome stays consistent.
//
// Important: the toggle button sits inside the .field label but
// type="button" stops the label's implicit click from focusing the
// input (which would defocus mid-toggle on some browsers).

export interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  /** new-password (signup, reset) or current-password (login). Drives
   *  password-manager behavior. 'off' is for secrets that are NOT the
   *  viewer's own password — an api token pasted from another product, which
   *  a password manager should neither offer to fill nor offer to save. */
  autoComplete: 'new-password' | 'current-password' | 'off';
  required?: boolean;
  minLength?: number;
  placeholder?: string;
  /** Surfaces ID for screen-reader labels when needed; usually the
   *  surrounding <label> covers this. */
  id?: string;
}

export function PasswordInput({
  value,
  onChange,
  autoComplete,
  required,
  minLength = 8,
  placeholder,
  id,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        // Extra right padding so the eye button doesn't overlap the
        // last character of long passwords.
        style={{ paddingRight: 44 }}
        className="w-full"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'hide password' : 'show password'}
        aria-pressed={visible}
        className="tap-44 absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--ink)]/55 transition-colors hover:bg-[var(--paper)] hover:text-[var(--ink)] focus:bg-[var(--paper)] focus:outline-none"
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 3l18 18" />
      <path d="M10.585 6.087A10.5 10.5 0 0 1 12 6c6.5 0 10 6 10 6a17.7 17.7 0 0 1-2.9 3.7" />
      <path d="M6.61 6.61A17.6 17.6 0 0 0 2 12s3.5 6 10 6c1.41 0 2.7-.28 3.86-.77" />
      <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
    </svg>
  );
}
